import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './InfoTooltip.css';

const VIEWPORT_MARGIN = 8;
const TRIGGER_GAP = 8;

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const InfoTooltip = ({ text }) => {
  const tooltipId = useId();
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [position, setPosition] = useState(null);
  const isOpen = isHovered || isFocused;

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!triggerRef.current || !tooltipRef.current) return;

      const trigger = triggerRef.current.getBoundingClientRect();
      const tooltip = tooltipRef.current.getBoundingClientRect();
      const maximumLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - tooltip.width - VIEWPORT_MARGIN);
      const left = clamp(
        trigger.left + (trigger.width / 2) - (tooltip.width / 2),
        VIEWPORT_MARGIN,
        maximumLeft
      );
      const spaceAbove = trigger.top - VIEWPORT_MARGIN;
      const spaceBelow = window.innerHeight - trigger.bottom - VIEWPORT_MARGIN;
      const placeAbove = spaceAbove >= tooltip.height + TRIGGER_GAP || spaceAbove >= spaceBelow;
      const preferredTop = placeAbove
        ? trigger.top - tooltip.height - TRIGGER_GAP
        : trigger.bottom + TRIGGER_GAP;
      const maximumTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - tooltip.height - VIEWPORT_MARGIN);

      setPosition({
        left,
        top: clamp(preferredTop, VIEWPORT_MARGIN, maximumTop)
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, text]);

  const tooltip = isOpen && typeof document !== 'undefined' ? createPortal(
    <span
      id={tooltipId}
      ref={tooltipRef}
      role="tooltip"
      className={`info-tooltip__text${position ? ' info-tooltip__text--visible' : ''}`}
      style={position || undefined}
      dangerouslySetInnerHTML={{ __html: text }}
    />,
    document.body
  ) : null;

  return (
    <span className="info-tooltip">
      <button
        ref={triggerRef}
        type="button"
        className="info-tooltip__trigger"
        aria-label="More information"
        aria-describedby={isOpen ? tooltipId : undefined}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      >
        ⓘ
      </button>
      {tooltip}
    </span>
  );
};

export default React.memo(InfoTooltip);
